import * as THREE from 'three';
import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';

/**
 * The split horizon bar (SPEC §3.4.1) and the geometry every other attitude element hangs off.
 *
 * The horizon is derived analytically from the camera orientation rather than from the aircraft's
 * pitch and bank. For a pinhole camera the true horizon — the set of directions with zero
 * elevation — always projects to a *straight line*, and its equation follows directly from where
 * world-up sits in camera space. Deriving it this way is exact at any attitude, costs nothing,
 * and keeps working when the camera is displaced by the 4° install tilt or by look-around, all of
 * which would need special cases in a pitch-and-bank formulation.
 *
 * Angles in radians, lengths in pixels.
 */

export interface HorizonGeometry {
  /** Screen rotation of the horizon line, radians, for `ctx.rotate`. */
  rollRad: number;
  /**
   * Signed perpendicular offset of the horizon from screen centre, pixels, measured along the
   * rotated down axis. Positive means the horizon sits below centre — the aircraft is nose-up.
   */
  offsetPx: number;
  /** False when the camera is pointing so far up or down that the horizon has no useful line. */
  valid: boolean;
}

export function createHorizonGeometry(): HorizonGeometry {
  return { rollRad: 0, offsetPx: 0, valid: false };
}

const upInCameraSpace = new THREE.Vector3();
const inverseOrientation = new THREE.Quaternion();
const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * Solve the horizon line for a camera orientation.
 *
 * In centred screen coordinates (u right, v down) a direction is `(u, -v, -f)` in camera space,
 * and lies on the horizon when it is perpendicular to world-up. Substituting gives the line
 *
 *     u·upCam.x − v·upCam.y − f·upCam.z = 0
 *
 * from which the roll angle and the perpendicular offset fall straight out.
 */
export function computeHorizon(
  cameraOrientation: THREE.Quaternion,
  focalPx: number,
  out: HorizonGeometry,
): HorizonGeometry {
  inverseOrientation.copy(cameraOrientation).invert();
  upInCameraSpace.copy(WORLD_UP).applyQuaternion(inverseOrientation);

  const a = upInCameraSpace.x;
  const b = -upInCameraSpace.y;
  const c = -focalPx * upInCameraSpace.z;

  const normal = Math.hypot(a, b);
  if (normal < 1e-9) {
    // Looking straight up or straight down: the horizon is a circle at infinity, not a line.
    out.valid = false;
    out.rollRad = 0;
    out.offsetPx = 0;
    return out;
  }

  out.rollRad = Math.atan2(a, -b);
  out.offsetPx = c / normal;
  out.valid = true;
  return out;
}

/**
 * Draw the split bar: two horizontal segments either side of a centre gap, each with a short
 * tick dropped from its outer end. The gap is what keeps the reticle and flight path marker
 * readable through the middle of the display.
 */
export function drawHorizon(painter: HudPainter, horizon: HorizonGeometry): void {
  if (!horizon.valid) return;

  const layout = painter.layout;
  const ctx = painter.ctx;
  const gap = layout.px(HUD.horizonGapPx) * 0.5;
  const length = layout.px(HUD.horizonBarLengthPx);
  const drop = layout.px(HUD.horizonTipDropPx);
  const y = horizon.offsetPx;

  ctx.save();
  ctx.translate(layout.centreX, layout.centreY);
  ctx.rotate(horizon.rollRad);

  ctx.beginPath();
  ctx.moveTo(-gap - length, y);
  ctx.lineTo(-gap, y);
  ctx.lineTo(-gap, y + drop);
  ctx.moveTo(gap + length, y);
  ctx.lineTo(gap, y);
  ctx.lineTo(gap, y + drop);
  painter.stroke();

  ctx.restore();
}
